import React, { useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { useParams } from 'react-router-dom';
import { IAmendmentPageState, IOwnerData } from '../..';
import { IAmendmentGeneralData } from '../../line-amendment';
import {
  fetchedPersonAddressList,
  fetchedPersonsWithPagination,
} from './queries';
import { isEmpty } from '@utils/validationUtils';
import { validatePolicyOwnerField } from './validation';
import ProposalPolicyPersonDrawer from '../../../proposal/proposal-policy-person-drawer/ProposalPolicyPersonDrawer';
import PersonAddressDrawer from '@modules/salesforce/persons/person-details-page/person-address-drawer/PersonAddressDrawer';
import { extractPersonsAddresses, extractPersonsFromQuery } from './utils';
import WithLinkFormField from '@components/form-fields/WithLinkFormField';
import SelectFormField from '@components/form-fields/SelectFormField';
import AutocompleteFormField from '@components/form-fields/AutocompleteFormField';
import { IAbstractRecord } from '@models';
import { cloneDeep } from 'lodash';
import WidgetSection from '@components/widgets/display-widget/WidgetSection';

interface IOwnerWidgetProps {
  generalData: IAmendmentGeneralData;
  pageState: IAmendmentPageState;
  onPageStateUpdate: (pageState: IAmendmentPageState) => void;
  isDisabled: boolean;
}

const OwnerWidget: React.FC<IOwnerWidgetProps> = ({
  generalData,
  pageState,
  onPageStateUpdate,
  isDisabled,
}) => {
  const { id: currentAmendmentID } = useParams();

  const [newPersonDrawerOpen, setNewPersonDrawerOpen] =
    useState<boolean>(false);
  const [newAddressDrawerOpen, setNewAddressDrawerOpen] =
    useState<boolean>(false);
  const [currentSection, setCurrentSection] = useState<string | null>(null);

  const [lists, setLists] = useState<
    Record<string, Record<string, Record<string, string> | string>>
  >({
    ownerId: {},
    ownerAddresses: {},
  });

  const values = pageState.values.policyPersons.owner;
  const errors = pageState.errors.policyPersons.owner;
  const touched = pageState.touched.policyPersons.owner;

  const [fetchedPersonsLazy] = useLazyQuery(fetchedPersonsWithPagination);
  const [getPersonAddressListLazy] = useLazyQuery(fetchedPersonAddressList);

  const handleOpenDrawer = (section: string) => {
    setCurrentSection(section);
    setNewPersonDrawerOpen(true);
  };

  const handleOpenAddressDrawer = () => {
    setNewAddressDrawerOpen(true);
  };

  const initialize = async () => {
    const newOwnerList: Record<string, string> = {};
    newOwnerList[values.ownerId] = values.fullName;
    const newList = {
      ownerId: { payerPersons: newOwnerList },
    };
    setLists(newList);
  };
  useEffect(() => {
    initialize();
  }, []);

  const handleOwnerAddresses = async (personId: string) => {
    const result = await getPersonAddressListLazy({
      variables: {
        selectedPersonID: personId,
      },
      errorPolicy: 'all',
    });

    return extractPersonsAddresses(result.data);
  };

  const onFieldUpdate = async (
    fieldName: keyof IOwnerData,
    value: unknown,
    rowIndex?: number,
    touched = false,
    updatedList: typeof lists = lists
  ) => {
    const newPageState = cloneDeep(pageState);
    if (fieldName === 'ownerId') {
      (newPageState.values.policyPersons.owner as any)[fieldName] = value;
      newPageState.errors.policyPersons.owner[fieldName] =
        validatePolicyOwnerField(fieldName, value);
      if (touched) {
        newPageState.touched.policyPersons.owner[fieldName] = true;
      }

      if (!isEmpty(value)) {
        const newLists = { ...updatedList };
        const result2 = (await handleOwnerAddresses(value as string)) as Record<
          string,
          Record<string, { address: string; isPrimary: boolean }>
        >;
        const ownerAddresses = result2.ownerPersonAddress;
        const extractedAddresses: IAbstractRecord = {};
        Object.keys(ownerAddresses).forEach((key) => {
          extractedAddresses[key] = ownerAddresses[key].address;
          if (ownerAddresses[key].isPrimary) {
            newPageState.values.policyPersons.owner.ownerAddress = key;
          }
        });
        newPageState.errors.policyPersons.owner.ownerAddress = '';
        newLists['ownerAddresses'] = extractedAddresses;
        setLists(newLists);
      }
    } else {
      (newPageState.values.policyPersons.owner as any)[fieldName] = value;
      newPageState.errors.policyPersons.owner[fieldName] =
        validatePolicyOwnerField(fieldName, value);
      if (touched) {
        newPageState.touched.policyPersons.owner[fieldName] = true;
      }
    }
    onPageStateUpdate(newPageState);
  };

  const handleDrawerSubmitSuccess = (
    person: string,
    newPersonFullName: string
  ) => {
    const newPersonList: Record<string, string> = {};
    newPersonList[person] = newPersonFullName;
    onFieldUpdate('ownerId', [person], 0, false, {
      ownerId: { ownerPersons: newPersonList },
      ownerAddresses: {},
    });
    setNewPersonDrawerOpen(false);
  };

  const handleAddressSubmitSuccess = (
    address: string,
    addressFullName: string
  ) => {
    onFieldUpdate('ownerAddress', address, 0, false, {
      ownerAddresses: { [address]: addressFullName },
    });
    setNewAddressDrawerOpen(false);
  };

  const getDefaultPersonListIfEmpty = (
    list: Record<string, string>,
    owner: IOwnerData
  ) => {
    if (Object.keys(list || {}).length > 0 || !owner.fullName) {
      return list;
    }

    const newList: Record<string, string> = {
      [owner.ownerId]: owner.fullName,
      [owner.ownerAddress]: owner.addressDetails,
    };

    return newList;
  };

  return (
    <div>
      <WidgetSection isFieldsGrid fieldsPerRow={2}>
        {newPersonDrawerOpen && currentSection === 'owner' && (
          <ProposalPolicyPersonDrawer
            open={newPersonDrawerOpen}
            onClose={() => setNewPersonDrawerOpen(false)}
            section={currentSection}
            successRoute="/production/amendment/"
            proposalId={currentAmendmentID}
            lineExternalCode={generalData.line.externalCode}
            onSubmitSuccess={handleDrawerSubmitSuccess}
          />
        )}

        {newAddressDrawerOpen && (
          <PersonAddressDrawer
            open={newAddressDrawerOpen}
            onClose={() => setNewAddressDrawerOpen(false)}
            personId={values.ownerId}
            onSubmitSuccess={handleAddressSubmitSuccess}
          />
        )}
        <WithLinkFormField
          linkText={isDisabled ? '' : 'Add Person'}
          onLinkClick={() => {
            handleOpenDrawer('owner');
          }}
        >
          <AutocompleteFormField
            key="ownerId"
            name="ownerId"
            title="Owner"
            placeholder="Owner"
            value={values.ownerId}
            error={touched.ownerId ? errors.ownerId : ''}
            disabled={isDisabled}
            material
            onChange={(v) => {
              onFieldUpdate('ownerId', v);
            }}
            fetchOptions={function (
              value,
              pagination,
              hasDefaultValue
            ): Promise<{
              options: Record<string, string>;
              totalCount: number;
            }> {
              const defaultValue = hasDefaultValue ? value : null;
              return fetchedPersonsLazy({
                variables: {
                  searchKeyword: hasDefaultValue ? null : value,
                  pagination,
                  selectedPersonID: defaultValue,
                },
              }).then((result) => {
                const options = extractPersonsFromQuery(
                  result.data?.Production?.queries
                    ?.GetAssignedPersonsWithPagination?.items
                );
                return {
                  options,
                  totalCount:
                    result.data?.Production?.queries
                      ?.GetAssignedPersonsWithPagination?.paging?.totalCount,
                };
              });
            }}
          />
        </WithLinkFormField>
        <WithLinkFormField
          linkText={isDisabled ? '' : 'Add Address'}
          onLinkClick={() => {
            handleOpenAddressDrawer();
          }}
        >
          <SelectFormField
            key="ownerAddress"
            name="ownerAddress"
            title="Owner Address"
            placeholder="Owner Address"
            value={values.ownerAddress}
            error={touched.ownerAddress ? errors.ownerAddress : ''}
            material
            disabled={isDisabled}
            onChange={(v) => {
              onFieldUpdate('ownerAddress', v);
            }}
            selectOptions={getDefaultPersonListIfEmpty(
              lists.ownerAddresses as Record<string, string>,
              values
            )}
          />
        </WithLinkFormField>
      </WidgetSection>
    </div>
  );
};

export default OwnerWidget;
